import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/apiClient";
import { PageHead, Card, Pill, HealthDot, Loading, Err } from "./_shared";
import { Settings, ShieldCheck, KeyRound, Cpu, ToggleRight, BellRing, ServerCog } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

export default function AdminSettings() {
  const { user } = useAuth();
  const [health, setHealth] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/api/admin/dashboard")
      .then((d) => setHealth({ env: d.environment, health: d.health, providers: d.kpis }))
      .catch((e) => setErr(e.message));
  }, []);

  if (err) return <Err msg={err} />;
  if (!health) return <Loading />;

  return (
    <div data-testid="admin-settings">
      <PageHead title="Admin settings" sub={`Environment · ${health.env}`} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Card title="Platform environment">
          <KV k="Environment" v={<Pill tone={health.env === "production" ? "danger" : health.env === "staging" ? "warn" : "success"}>{health.env}</Pill>} />
          <KV k="API" v={<HealthDot status={health.health?.api} />} />
          <KV k="Worker" v={<HealthDot status={health.health?.worker} />} />
          <KV k="MongoDB" v={<HealthDot status={health.health?.mongo} />} />
          <KV k="Queue" v={<HealthDot status={health.health?.queue} />} />
          <KV k="Storage" v={<HealthDot status={health.health?.storage} />} />
        </Card>

        <Card title="Security &amp; auth">
          <KV k="Signed in as" v={user?.email} />
          <KV k="Primary role" v={<Pill tone="lime">{(user?.roles || [])[0] || "—"}</Pill>} />
          <KV k="Auth method" v="JWT · bcrypt" />
          <KV k="JWT secret" v={<span style={{ color: "var(--pl-text-muted)" }}>configured · not exposed</span>} />
          <KV k="Brute-force lockout" v="enabled" />
          <KV k="Session length" v="24h sliding" />
        </Card>

        <Card title="Quick links" style={{ gridColumn: "1 / -1" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
            <QuickLink to="/admin/feature-flags"  icon={<ToggleRight size={14} />}  label="Feature flags" />
            <QuickLink to="/admin/model-providers" icon={<Cpu size={14} />}          label="Model providers" />
            <QuickLink to="/admin/support-mode"   icon={<ShieldCheck size={14} />}  label="Support Mode" />
            <QuickLink to="/admin/audit-logs"     icon={<KeyRound size={14} />}     label="Audit logs" />
            <QuickLink to="/admin/system-events"  icon={<BellRing size={14} />}     label="System events" />
            <QuickLink to="/admin/control"        icon={<Settings size={14} />}     label="Admin Control" />
            <QuickLink to="/admin/webhooks"       icon={<ServerCog size={14} />}    label="Webhooks" />
            <QuickLink to="/admin/automations"    icon={<ServerCog size={14} />}    label="Automations" />
          </div>
        </Card>
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: "1px dashed var(--pl-border)", fontSize: 13 }}>
      <span style={{ color: "var(--pl-text-muted)", fontWeight: 700 }}>{k}</span>
      <span>{v}</span>
    </div>
  );
}

function QuickLink({ to, icon, label }) {
  return (
    <Link to={to} style={{
      display: "flex", alignItems: "center", gap: 10, padding: "11px 12px",
      borderRadius: 10, border: "1px solid var(--pl-border)",
      background: "var(--pl-bg-2)", color: "var(--pl-text)", fontSize: 12.5, fontWeight: 700,
      textDecoration: "none",
    }}>
      <span style={{ color: "var(--pl-aqua)" }}>{icon}</span>{label}
    </Link>
  );
}
